import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiMutationKeys } from "#lib/AiQueryKeys.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { useMutation } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";

interface MutationPayload {
    fileId: string;
    fileUploadUrl: string;
    file: File;
}

export const useUploadFileMutation = (setFiles: Dispatch<SetStateAction<FileModel[]>>, conversationId?: string) => {
    const { completeConversationFileUpload, cancelConversationFileUpload, uploadFile } = useAiApiClient();
    const { trackEvent } = useTracking();

    return useMutation({
        mutationKey: AiMutationKeys.UploadFile(),
        mutationFn: async ({ file, fileUploadUrl, fileId }: MutationPayload) => {
            const abortController = new AbortController();

            setFiles(prevFiles => {
                const updatedFiles = prevFiles.map<FileModel>(prevFile =>
                    prevFile.id === fileId ? { ...prevFile, abortController, file } : prevFile
                );

                return updatedFiles;
            });

            await uploadFile(file, fileUploadUrl, abortController);

            // Complete the file upload
            const { filePath } = await completeConversationFileUpload({ fileId });

            trackEvent(MixpanelEvent.FileUploaded, {
                "File Extension": file.name.split(".").pop(),
                "File Size": file.size,
                "Conversation Id": conversationId
            });

            setFiles(prevFiles => {
                const updatedFiles = prevFiles.map<FileModel>(prevFile =>
                    prevFile.id === fileId
                        ? { ...prevFile, fileUploadStatus: "Completed", filePath, isIndexed: false, availability: "Processing" }
                        : prevFile
                );

                return updatedFiles;
            });
        },
        onError: (_, { fileId }) => {
            setFiles(prevFiles => {
                const updatedFiles = prevFiles.map<FileModel>(prevFile =>
                    prevFile.id === fileId ? { ...prevFile, fileUploadStatus: "Failed" } : prevFile
                );

                return updatedFiles;
            });

            cancelConversationFileUpload({ fileId });
        }
    });
};
